import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import managerAssessmentService, {
    type EvaluationPeriodPayload,
    type ManagerAssessmentAnswersPayload,
    type ManagerAssessmentDraftAnswersPayload,
    type ManagerAssessmentInsightsPayload,
    type ManagerAssessmentInsightsResponse,
    type ManagerAssessmentPayload,
    type MeetingTopicsPayload,
    type QuickAssessmentPayload,
} from './managerAssessmentService';

import type { Competency } from '@/types/competencies/Competency';
import type { CompetencyProfileResponse } from '@/types/competencies/CompetencyProfileApi';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { User } from '@/types/directory/User';
import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';
import type { AssessmentEmployee } from '@/types/manager-assessment/Employee';
import type { AssessmentTemplateQuestion } from '@/types/manager-assessment/Template';
import type { ManagerAssessmentCompetencyResultsResponse } from '@/types/review/CompetencyGroupResult';

import { useCompetencyProfileByEmployee, useGetCompetencyGroups } from '@/api/competency-services';
import { useGetEffectiveCompetencies } from '@/api/competency-services/employee-competency';
import { useGetCompanyPeople } from '@/api/directory';
import directoryService from '@/api/directory/directoryService';
import { REVIEW_DISCUSSION_QUERY_KEYS } from '@/api/review-services/review-discussion';
import { queryClient } from '@/AppWithProviders';
import {
    CycleInstanceQueryKey,
    ManagerAssessmentQueryKey,
    ReviewQueryKey,
} from '@/constants/QueryKeys';
import { getLocalizedText } from '@/utils/localization';
import { getProfilePictureUrl } from '@/utils/user';

// The directory API caps page size at 50 (COMPANY_USERS_MAX_PAGE_SIZE).
const EMPLOYEES_PAGE_SIZE = 50;
const MIN_SEARCH_LENGTH = 2;
/** How many employees a picker proposes before a search is long enough to query the directory. */
const SUGGESTED_EMPLOYEES_COUNT = 8;

interface SaveManagerAssessmentVariables {
    cycleInstanceId: string;
    payload: ManagerAssessmentPayload;
}

const useSaveManagerAssessment = () => {
    return useMutation<CycleInstance, Error, SaveManagerAssessmentVariables>({
        mutationFn: ({ cycleInstanceId, payload }) =>
            managerAssessmentService.saveManagerAssessment(cycleInstanceId, payload),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CycleInstanceQueryKey.CYCLE_INSTANCE],
            }),
    });
};

interface SaveManagerAssessmentAnswersVariables {
    cycleInstanceId: string;
    payload: ManagerAssessmentAnswersPayload;
}

const useSaveManagerAssessmentAnswers = () => {
    return useMutation<CycleInstance, Error, SaveManagerAssessmentAnswersVariables>({
        mutationFn: ({ cycleInstanceId, payload }) =>
            managerAssessmentService.saveManagerAssessmentAnswers(cycleInstanceId, payload),
        onSuccess: (_data, { cycleInstanceId }) => {
            queryClient.invalidateQueries({
                queryKey: [CycleInstanceQueryKey.CYCLE_INSTANCE],
            });
            queryClient.invalidateQueries({
                queryKey: [ManagerAssessmentQueryKey.COMPETENCY_RESULTS, cycleInstanceId],
            });
        },
    });
};

interface SaveManagerAssessmentDraftAnswersVariables {
    cycleInstanceId: string;
    payload: ManagerAssessmentDraftAnswersPayload;
    // Client-side only — not sent to the server. Tells onSuccess whether this save just went through
    // the backend's post-submission recompute (#15643), so it knows whether there's anything new to
    // invalidate; not needed pre-submission, where the survey renders purely from local state.
    alreadySubmitted: boolean;
}

/**
 * Autosave for the in-progress survey. Before the first submission this deliberately does not touch the
 * query cache: the endpoint only writes the raw answer, and the survey already renders from local state —
 * invalidating here would refetch the whole cycle instance on every question navigation for nothing.
 * Once the survey has already been submitted, the endpoint recomputes Results from the edited answer
 * (#15643), so that save invalidates the cycle instance + competency results queries too, the same as a
 * submit, so a manager who is already on the Results step sees the change without a reload.
 */
const useSaveManagerAssessmentDraftAnswers = () => {
    return useMutation<void, Error, SaveManagerAssessmentDraftAnswersVariables>({
        mutationFn: ({ cycleInstanceId, payload }) =>
            managerAssessmentService.saveManagerAssessmentDraftAnswers(cycleInstanceId, payload),
        onSuccess: (_data, { cycleInstanceId, alreadySubmitted }) => {
            if (!alreadySubmitted) {
                return;
            }
            queryClient.invalidateQueries({
                queryKey: [CycleInstanceQueryKey.CYCLE_INSTANCE],
            });
            queryClient.invalidateQueries({
                queryKey: [ManagerAssessmentQueryKey.COMPETENCY_RESULTS, cycleInstanceId],
            });
        },
    });
};

interface SaveMeetingTopicsVariables {
    cycleInstanceId: string;
    payload: MeetingTopicsPayload;
}

const useSaveMeetingTopics = () => {
    return useMutation<CycleInstance, Error, SaveMeetingTopicsVariables>({
        mutationFn: ({ cycleInstanceId, payload }) =>
            managerAssessmentService.saveMeetingTopics(cycleInstanceId, payload),
        // The endpoint returns the updated CycleInstance, so write it straight into the cache
        // instead of invalidating (avoids a redundant refetch).
        onSuccess: (data, { cycleInstanceId }) => {
            queryClient.setQueryData([CycleInstanceQueryKey.CYCLE_INSTANCE, cycleInstanceId], data);
        },
    });
};

interface SaveEvaluationPeriodVariables {
    cycleInstanceId: string;
    payload: EvaluationPeriodPayload;
}

const useSaveEvaluationPeriod = () => {
    return useMutation<CycleInstance, Error, SaveEvaluationPeriodVariables>({
        mutationFn: ({ cycleInstanceId, payload }) =>
            managerAssessmentService.saveEvaluationPeriod(cycleInstanceId, payload),
        onSuccess: (data, { cycleInstanceId }) => {
            queryClient.setQueryData([CycleInstanceQueryKey.CYCLE_INSTANCE, cycleInstanceId], data);
        },
    });
};

interface SaveQuickAssessmentVariables {
    cycleInstanceId: string;
    payload: QuickAssessmentPayload;
}

const useSaveQuickAssessment = () => {
    return useMutation<CycleInstance, Error, SaveQuickAssessmentVariables>({
        mutationFn: ({ cycleInstanceId, payload }) =>
            managerAssessmentService.saveQuickAssessment(cycleInstanceId, payload),
        onSuccess: (data, { cycleInstanceId }) => {
            queryClient.setQueryData([CycleInstanceQueryKey.CYCLE_INSTANCE, cycleInstanceId], data);
            queryClient.invalidateQueries({
                queryKey: [ManagerAssessmentQueryKey.COMPETENCY_RESULTS, cycleInstanceId],
            });
        },
    });
};

interface AdvanceManagerAssessmentStepVariables {
    cycleInstanceId: string;
    step: number;
}

// Advancing the stepper to a reached mid-flow step (3 = Prepare Meeting, 4 = Meet) is a
// forward-only, idempotent operation on the backend, so the caller just fires it on page load.
// The response carries the updated CycleInstance (now at the reached step), which we write into
// the cache and use to refresh the dashboard lists so the ongoing review shows the right progress.
const useAdvanceManagerAssessmentStep = () => {
    return useMutation<CycleInstance, Error, AdvanceManagerAssessmentStepVariables>({
        mutationFn: ({ cycleInstanceId, step }) =>
            managerAssessmentService.advanceManagerAssessmentStep(cycleInstanceId, step),
        onSuccess: (data, { cycleInstanceId }) => {
            queryClient.setQueryData([CycleInstanceQueryKey.CYCLE_INSTANCE, cycleInstanceId], data);
            queryClient.invalidateQueries({ queryKey: [ReviewQueryKey.ONGOING_REVIEWS_PAGED] });
            queryClient.invalidateQueries({ queryKey: [ReviewQueryKey.PAST_REVIEWS_PAGED] });
        },
    });
};

// The manager assessment reuses the directory people API (scoped to the manager's reports)
// instead of the removed /assessment/employees spike endpoint.
const toAssessmentEmployee = (user: User): AssessmentEmployee => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    avatarUrl: getProfilePictureUrl(user.profilePictureUrl) || null,
    jobTitleId: user.jobTitleId,
    hasProfile: user.hasProfile ?? false,
});

const matchesEmployeeName = (employee: Pick<User, 'firstName' | 'lastName'>, search: string) =>
    `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(search.toLowerCase());

interface AssessmentEmployeesOptions {
    canManageAllDepartmentsPerformance?: boolean;
    includeHierarchy?: boolean;
    /**
     * Whether to propose a short list of employees while the search is too short to query the
     * directory, filtered by name as the first characters are typed. Off by default: the picker
     * then stays silent until there is a real search. @default false
     */
    suggestBeforeSearch?: boolean;
}

// Search-driven: only queries the directory once the manager types (>= 2 chars),
// rather than eagerly loading everyone up front — unless the caller asks for suggestions.
// When includeHierarchy is true and canManageAllDepartmentsPerformance is false,
// fetches the entire manager hierarchy (direct + indirect reports).
const ASSESSMENT_EMPLOYEES_HIERARCHY_QUERY_KEY = 'assessment-employees-hierarchy';

const useGetAssessmentEmployees = (search?: string, options?: AssessmentEmployeesOptions) => {
    const trimmed = search?.trim() ?? '';
    const hasSearch = trimmed.length >= MIN_SEARCH_LENGTH;
    const isSuggesting = (options?.suggestBeforeSearch ?? false) && !hasSearch;
    const isEnabled = hasSearch || isSuggesting;
    // If the caller supplied an options object, defer to its permission value (which may
    // be undefined while permissions are still loading). When no options are supplied,
    // default to the manager's own team scope.
    const canViewAll = options ? options.canManageAllDepartmentsPerformance : false;
    const includeHierarchy = options?.includeHierarchy ?? false;

    const query = useGetCompanyPeople(
        {
            pageNumber: 1,
            pageSize: isSuggesting ? SUGGESTED_EMPLOYEES_COUNT : EMPLOYEES_PAGE_SIZE,
            isActiveOnly: true,
            includeInvited: true,
            searchQuery: hasSearch ? trimmed : undefined,
        },
        { canViewResultsOfAllDepartments: canViewAll, enabled: isEnabled },
    );

    // When includeHierarchy is true, we need to fetch all manager employees (including indirect reports)
    // and then filter by search query client-side
    const hierarchyQuery = useQuery<AssessmentEmployee[]>({
        // Every suggestion list is the same list whatever was half-typed: one cache entry for all.
        queryKey: [ASSESSMENT_EMPLOYEES_HIERARCHY_QUERY_KEY, hasSearch ? trimmed : ''],
        queryFn: async () => {
            if (!isEnabled || !includeHierarchy) return [];

            // Fetch all company employees to build the hierarchy
            const allEmployees = await directoryService.getAllCompanyUsers({
                isActiveOnly: true,
                includeInvited: true,
            });

            // Get the current user's ID to start the hierarchy traversal
            const currentUser = await directoryService.getUserById('me');

            // Recursively collect all direct and indirect reports
            const hierarchyEmployees = new Set<string>();
            const collectSubEmployees = (managerId: string) => {
                const directReports = allEmployees.filter((emp) => emp.managerId === managerId);
                directReports.forEach((emp) => {
                    if (!hierarchyEmployees.has(emp.id)) {
                        hierarchyEmployees.add(emp.id);
                        collectSubEmployees(emp.id);
                    }
                });
            };

            collectSubEmployees(currentUser.id);

            // Filter by search query and convert to AssessmentEmployee
            const reports = allEmployees.filter(
                (user) =>
                    hierarchyEmployees.has(user.id) &&
                    (!hasSearch || matchesEmployeeName(user, trimmed)),
            );
            return (hasSearch ? reports : reports.slice(0, SUGGESTED_EMPLOYEES_COUNT)).map(
                toAssessmentEmployee,
            );
        },
        enabled: isEnabled && includeHierarchy && !canViewAll,
    });

    // The suggestions are fetched without a search; the one or two characters typed so far
    // narrow them here rather than going to the directory.
    const narrowSuggestions = (employees: AssessmentEmployee[]) =>
        isSuggesting && trimmed
            ? employees.filter((employee) => matchesEmployeeName(employee, trimmed))
            : employees;

    // Use hierarchyQuery if includeHierarchy is enabled, otherwise use the paginated query
    if (includeHierarchy && !canViewAll) {
        return {
            ...hierarchyQuery,
            data: narrowSuggestions(hierarchyQuery.data ?? []),
        };
    }

    return {
        ...query,
        data: narrowSuggestions(query.data?.items.map(toAssessmentEmployee) ?? []),
    };
};

// Flattens an assigned competency profile into the page's EmployeeCompetency shape.
// lastAssessedProficiencyLevelId and lastAssessedAt are populated from the employee's
// most recent completed manager assessments (backend enriches the response for US #12742).
// Filters out employee-excluded competencies (Bug 13976) and catalog-deactivated ones
// so they are not offered for new manager assessments (history stays on skillset).
const profileToCompetencies = (profile: CompetencyProfileResponse): EmployeeCompetency[] => {
    return profile.groups.flatMap((group) =>
        group.competencies
            .filter(
                (competency) =>
                    !competency.isRemoved &&
                    competency.status !== 'Inactive' &&
                    competency.isIndividuallyDeactivated !== true,
            )
            .map((competency) => ({
                id: competency.id,
                name: getLocalizedText(competency.name),
                description: null,
                groupName: getLocalizedText(group.categoryName),
                lastAssessedAt: competency.lastAssessedAt ?? null,
                lastAssessedProficiencyLevelId: competency.lastAssessedProficiencyLevelId ?? null,
                questionCount: competency.questionCount ?? 0,
                isDeactivated: false,
            })),
    );
};

// The employee's competencies come from their assigned competency profile, resolved
// server-side in a single call (assignment -> profile -> competencies).
// The backend now includes isRemoved flag for competencies removed for this employee (Bug 13976).
// Additionally fetches the effective competency list (profile - overlay removals + overlay
// additions) to ensure individually-added competencies are included — Bug 14081.
const useGetEmployeeCompetencies = (employeeId?: string) => {
    const { data: profile, isLoading: profileLoading } = useCompetencyProfileByEmployee(employeeId);
    const { data: effective, isLoading: effectiveLoading } =
        useGetEffectiveCompetencies(employeeId);
    // Competency catalog provides group name and metadata for individually-added competencies.
    const { data: groups = [] } = useGetCompetencyGroups();

    const data = useMemo(() => {
        // Profile competencies are enriched with lastAssessedAt / proficiency data.
        const profileCompetencies = profile ? profileToCompetencies(profile) : [];

        if (!effective || effective.length === 0) return profileCompetencies;

        // Individually-added competencies: present in effective but not in the profile response.
        const profileIds = new Set(profileCompetencies.map((c) => c.id));
        const individualIds = effective
            .filter((ec) => !profileIds.has(ec.competencyId))
            .map((ec) => ec.competencyId);

        if (individualIds.length === 0) return profileCompetencies;

        // Build a catalog lookup to resolve group name and metadata for added competencies.
        const catalogLookup = new Map<string, { competency: Competency; groupName: string }>();
        groups.forEach((group) => {
            const gName = getLocalizedText(group.name);
            group.competencies.forEach((comp) => {
                catalogLookup.set(comp.id, { competency: comp, groupName: gName });
            });
        });

        const additionalCompetencies: EmployeeCompetency[] = individualIds
            .map((id) => {
                const catalogEntry = catalogLookup.get(id);
                const effectiveEntry = effective.find((e) => e.competencyId === id);
                return {
                    id,
                    name: catalogEntry
                        ? getLocalizedText(catalogEntry.competency.name)
                        : getLocalizedText(effectiveEntry?.name),
                    description: catalogEntry?.competency.description
                        ? getLocalizedText(catalogEntry.competency.description)
                        : null,
                    groupName: catalogEntry?.groupName ?? null,
                    lastAssessedAt: null,
                    lastAssessedProficiencyLevelId: null,
                    questionCount: 0,
                    isDeactivated: catalogEntry
                        ? catalogEntry.competency.status === 'Inactive' ||
                          catalogEntry.competency.isIndividuallyDeactivated === true
                        : false,
                };
            })
            .filter((c) => !c.isDeactivated);

        return [...profileCompetencies, ...additionalCompetencies];
    }, [profile, effective, groups]);

    return {
        data,
        isLoading: !!employeeId && (profileLoading || effectiveLoading),
    };
};

const toEmployeeCompetency = (
    competency: Competency,
    groupName: string | null,
): EmployeeCompetency => ({
    id: competency.id,
    name: getLocalizedText(competency.name),
    description: competency.description ? getLocalizedText(competency.description) : null,
    groupName,
    lastAssessedAt: null,
    lastAssessedProficiencyLevelId: null,
    // The org library (/competency) doesn't expose a question count; profile competencies do.
    questionCount: 0,
    isDeactivated:
        competency.status === 'Inactive' || competency.isIndividuallyDeactivated === true,
});

// The org competency library, used by the "Add skills" modal. Lazy: competencies and their
// groups are only fetched when enabled (the modal is open), not eagerly on page load. The
// group name is resolved from competencyGroupId so added skills land in the right category.
const useGetAllCompetencies = (enabled = false) => {
    // Competency groups already nest their competencies, so a single call gives us both the
    // competency list and each competency's group name — no separate useGetCompetencies needed.
    const { data: groups = [], ...rest } = useGetCompetencyGroups({ enabled });

    return {
        ...rest,
        data: useMemo(
            () =>
                groups.flatMap((group) =>
                    group.competencies.map((competency) =>
                        toEmployeeCompetency(competency, getLocalizedText(group.name)),
                    ),
                ),
            [groups],
        ),
    };
};

const useGetEvaluationPeriods = () => {
    const { t } = useTranslation();
    const query = useQuery({
        queryKey: [ManagerAssessmentQueryKey.EVALUATION_PERIODS],
        queryFn: managerAssessmentService.getEvaluationPeriods,
    });

    const translatedData = useMemo(
        () =>
            query.data?.map((period) => ({
                ...period,
                name: t(period.name as never),
            })),
        [query.data, t],
    );

    return { ...query, data: translatedData };
};

// Lazy: only fetched when the "Use a template" method is selected.
const useGetAssessmentTemplates = (enabled = false) => {
    return useQuery({
        queryKey: [ManagerAssessmentQueryKey.ASSESSMENT_TEMPLATES],
        queryFn: managerAssessmentService.getTemplates,
        enabled,
    });
};

const useGetTemplateQuestions = (templateId?: string | null) => {
    return useQuery<AssessmentTemplateQuestion[]>({
        queryKey: [ManagerAssessmentQueryKey.TEMPLATE_QUESTIONS, templateId],
        queryFn: () => managerAssessmentService.getTemplateQuestions(templateId!),
        enabled: !!templateId,
    });
};

const useUpdatePerformanceScore = (reviewInstanceId: string, cycleInstanceId: string) => {
    return useMutation<
        void,
        Error,
        { reviewInstanceId: string; overallPerformanceLevelId: string }
    >({
        mutationKey: [ManagerAssessmentQueryKey.UPDATE_PERFORMANCE_SCORE, reviewInstanceId],
        mutationFn: ({ overallPerformanceLevelId }) =>
            managerAssessmentService.updatePerformanceScore(
                reviewInstanceId,
                overallPerformanceLevelId,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ManagerAssessmentQueryKey.COMPETENCY_RESULTS, cycleInstanceId],
                refetchType: 'all',
            });

            if (reviewInstanceId) {
                queryClient.invalidateQueries({
                    queryKey: REVIEW_DISCUSSION_QUERY_KEYS.discussion(reviewInstanceId),
                    refetchType: 'all',
                });
                queryClient.invalidateQueries({
                    queryKey: REVIEW_DISCUSSION_QUERY_KEYS.handoutPreview(reviewInstanceId),
                    refetchType: 'all',
                });
            }
        },
    });
};

const useRequestManagerAssessmentInsights = () => {
    return useMutation<ManagerAssessmentInsightsResponse, Error, ManagerAssessmentInsightsPayload>({
        mutationFn: (payload) => managerAssessmentService.requestInsights(payload),
    });
};

const useGetManagerAssessmentCompetencyResults = (cycleInstanceId?: string) => {
    return useQuery<ManagerAssessmentCompetencyResultsResponse>({
        queryKey: [ManagerAssessmentQueryKey.COMPETENCY_RESULTS, cycleInstanceId],
        queryFn: () => managerAssessmentService.getCompetencyResults(cycleInstanceId!),
        enabled: !!cycleInstanceId,
    });
};

export {
    ASSESSMENT_EMPLOYEES_HIERARCHY_QUERY_KEY,
    useSaveManagerAssessment,
    useSaveManagerAssessmentAnswers,
    useSaveManagerAssessmentDraftAnswers,
    useSaveMeetingTopics,
    useSaveEvaluationPeriod,
    useSaveQuickAssessment,
    useAdvanceManagerAssessmentStep,
    useGetAssessmentEmployees,
    useGetEmployeeCompetencies,
    useGetAllCompetencies,
    useGetEvaluationPeriods,
    useGetAssessmentTemplates,
    useGetTemplateQuestions,
    useUpdatePerformanceScore,
    useGetManagerAssessmentCompetencyResults,
    managerAssessmentService,
    ManagerAssessmentQueryKey,
    useRequestManagerAssessmentInsights,
};
